import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { SpeedGasMetrics } from '@/lib/api/speed';
import { hyperEvmPublicClient } from '@/lib/hyperEvmPublicClient';
import { queryKeys } from '@/lib/query';

import { ChainLoadSampler } from './chainLoadSampler';

const CHAIN_LOAD_STALE_TIME_MS = 3_000;
/** Poll fast while the feature is live so the bar tracks block-to-block load. */
const ACTIVE_REFETCH_INTERVAL_MS = 5_000;
/**
 * Slow poll while the feature is off/unknown. Still non-zero so the indicator
 * auto-recovers if ops flip `/speed/gas` on without a page reload.
 */
const sampler = new ChainLoadSampler();
let latestMetrics: SpeedGasMetrics = { enabled: false };

/** Utilization thresholds (fraction of block gas used). */
const LOW_MAX = 0.5;
const HIGH_MIN = 0.85;

export type ChainLoadLevel = 'low' | 'mid' | 'high';

export interface ChainLoad {
    /** True only when `/speed/gas` parsed and returned `enabled: true`. */
    active: boolean;
    level: ChainLoadLevel;
    /** `avg1m ?? current` — smooths block-to-block flicker. Null when inactive. */
    displayLoad: number | null;
    /** The raw enabled payload, or null when inactive. */
    metrics: SpeedGasMetrics | null;
}

const INACTIVE: ChainLoad = {
    active: false,
    level: 'low',
    displayLoad: null,
    metrics: null,
};

/** Maps a `0..1` utilization to a discrete load band. Null → `low`. */
export function levelForLoad(load: number | null): ChainLoadLevel {
    if (load === null || load < LOW_MAX) return 'low';
    if (load >= HIGH_MIN) return 'high';
    return 'mid';
}

/**
 * Pure mapping from the raw `/speed/gas` payload to the view model. Returns an
 * inactive result unless the payload parsed with `enabled: true`, so a disabled
 * flag, missing data, or an error all collapse to "render nothing".
 */
export function deriveChainLoad(
    metrics: SpeedGasMetrics | undefined | null,
): ChainLoad {
    if (!metrics || metrics.enabled !== true) {
        return INACTIVE;
    }
    const displayLoad = metrics.avg1m ?? metrics.current;
    return {
        active: true,
        level: levelForLoad(displayLoad),
        displayLoad,
        metrics,
    };
}

/**
 * Samples public HyperEVM block headers in the active browser. Background tabs
 * pause automatically, so chain-load visibility costs no backend RPC calls.
 */
export function useChainLoad(): ChainLoad {
    const query = useQuery({
        queryKey: queryKeys.speed.gas(),
        queryFn: async () => {
            const block = await hyperEvmPublicClient.getBlock({
                blockTag: 'latest',
            });
            latestMetrics =
                sampler.push(
                    Date.now(),
                    block.number,
                    block.gasUsed,
                    block.gasLimit,
                ) ?? latestMetrics;
            return latestMetrics;
        },
        staleTime: CHAIN_LOAD_STALE_TIME_MS,
        refetchInterval: ACTIVE_REFETCH_INTERVAL_MS,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: 'always',
    });

    return useMemo(
        () => deriveChainLoad(query.data),
        [query.data],
    );
}
